import React, { useMemo, useState } from 'react';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import isoCountries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

isoCountries.registerLocale(enLocale);

// Chart colours — validated pair (dataviz validator: CVD ΔE 16.4, contrast ≥ 3:1
// on white; + amber for add-to-cart passes all pairs). Magnitude (bars, heatmap, tiles) uses the single indigo hue.
export const VIEWS_COLOR = '#4f46e5';
export const VISITORS_COLOR = '#0891b2';
export const CARTS_COLOR = '#d97706';

// Swiss cantons: ISO 3166-2:CH subdivision codes (what geoip-lite reports as
// `region`) → display name, plus a tile position for the cartogram. The grid
// approximates geography (north at top, Geneva bottom-left, Graubünden east).
export const CANTONS = {
  BS: { name: 'Basel-Stadt', col: 2, row: 0 },
  BL: { name: 'Basel-Landschaft', col: 3, row: 0 },
  AG: { name: 'Aargau', col: 4, row: 0 },
  ZH: { name: 'Zürich', col: 5, row: 0 },
  SH: { name: 'Schaffhausen', col: 6, row: 0 },
  TG: { name: 'Thurgau', col: 7, row: 0 },
  JU: { name: 'Jura', col: 1, row: 1 },
  SO: { name: 'Solothurn', col: 2, row: 1 },
  LU: { name: 'Luzern', col: 3, row: 1 },
  ZG: { name: 'Zug', col: 4, row: 1 },
  SZ: { name: 'Schwyz', col: 5, row: 1 },
  SG: { name: 'St. Gallen', col: 6, row: 1 },
  AR: { name: 'Appenzell A.Rh.', col: 7, row: 1 },
  AI: { name: 'Appenzell I.Rh.', col: 8, row: 1 },
  NE: { name: 'Neuchâtel', col: 1, row: 2 },
  BE: { name: 'Bern', col: 2, row: 2 },
  OW: { name: 'Obwalden', col: 3, row: 2 },
  NW: { name: 'Nidwalden', col: 4, row: 2 },
  UR: { name: 'Uri', col: 5, row: 2 },
  GL: { name: 'Glarus', col: 6, row: 2 },
  VD: { name: 'Vaud', col: 1, row: 3 },
  FR: { name: 'Fribourg', col: 2, row: 3 },
  VS: { name: 'Valais', col: 3, row: 3 },
  TI: { name: 'Ticino', col: 5, row: 3 },
  GR: { name: 'Graubünden', col: 6, row: 3 },
  GE: { name: 'Genève', col: 0, row: 4 },
};

export const cantonName = (code, unknownLabel) =>
  !code || code === 'Unknown' ? unknownLabel : CANTONS[code]?.name || code;

export const flagEmoji = (iso2) => {
  if (!iso2 || !/^[A-Za-z]{2}$/.test(iso2)) return '🏳️';
  return String.fromCodePoint(...[...iso2.toUpperCase()].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
};

export const countryName = (code, unknownLabel) =>
  !code || code === 'Unknown' ? unknownLabel : isoCountries.getName(code, 'en') || code;

export const formatNum = (v) => (Number(v) || 0).toLocaleString('de-CH');

// Blend light indigo → indigo by share of max (same ramp as the world map).
export function shade(value, max) {
  if (!value || !max) return '#f1f5f9';
  const t = Math.min(value / max, 1);
  const mix = (a, b) => Math.round(a + (b - a) * (0.15 + 0.85 * t));
  return `rgb(${mix(224, 79)}, ${mix(231, 70)}, ${mix(255, 229)})`;
}

export const Card = ({ title, icon: Icon, action, children, className = '' }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 ${className}`}>
    {(title || action) && (
      <div className="flex items-center justify-between gap-2 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4" />}
          {title}
        </h3>
        {action}
      </div>
    )}
    {children}
  </div>
);

export const EmptyState = ({ children }) => (
  <p className="text-sm text-gray-400 py-8 text-center">{children}</p>
);

/**
 * Change indicator. `unit` '%' for relative change, 'pp' for percentage
 * points (rates). null = no baseline → "new".
 */
export const Delta = ({ value, unit = '%', newLabel, title }) => {
  if (value === undefined) return null;
  if (value === null) {
    return newLabel ? (
      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{newLabel}</span>
    ) : null;
  }
  const up = value > 0;
  const down = value < 0;
  const cls = up ? 'text-green-700 bg-green-50' : down ? 'text-red-700 bg-red-50' : 'text-gray-600 bg-gray-100';
  const Icon = up ? ArrowUpIcon : down ? ArrowDownIcon : null;
  return (
    <span title={title} className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded tabular-nums ${cls}`}>
      {Icon && <Icon className="h-2.5 w-2.5" />}
      {up ? '+' : ''}
      {value.toFixed(1)}
      {unit === 'pp' ? ' pp' : '%'}
    </span>
  );
};

export const Kpi = ({ label, value, hint, delta }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
    <div className="flex items-start justify-between gap-2">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      {delta}
    </div>
    <p className="mt-1 text-2xl font-bold text-gray-900 tabular-nums">{value}</p>
    {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
  </div>
);

/**
 * Ranked horizontal bars. Rows are clickable when `onSelect` is given (used
 * for drill-down filters); the selected row is highlighted.
 */
export const RankedBars = ({ rows, onSelect, selected, limit = 8, emptyLabel, valueLabel }) => {
  const [expanded, setExpanded] = useState(false);
  if (!rows.length) return <EmptyState>{emptyLabel}</EmptyState>;
  const max = rows.reduce((m, r) => Math.max(m, r.value), 0) || 1;
  const total = rows.reduce((s, r) => s + r.value, 0) || 1;
  const shown = expanded ? rows : rows.slice(0, limit);
  return (
    <div>
      <ul className="space-y-1">
        {shown.map((r) => {
          const isSel = selected != null && selected === r.key;
          const Tag = onSelect ? 'button' : 'div';
          return (
            <li key={r.key}>
              <Tag
                type={onSelect ? 'button' : undefined}
                onClick={onSelect ? () => onSelect(isSel ? null : r.key) : undefined}
                title={`${r.label}: ${formatNum(r.value)} ${valueLabel || ''}`.trim()}
                className={`w-full text-left rounded-lg px-2 py-1.5 transition-colors ${
                  onSelect ? 'hover:bg-gray-50 cursor-pointer' : ''
                } ${isSel ? 'bg-indigo-50 ring-1 ring-indigo-200' : ''}`}
              >
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="flex items-center gap-2 min-w-0 text-gray-700">
                    {r.prefix && <span className="text-base leading-none shrink-0">{r.prefix}</span>}
                    <span className="truncate">{r.label}</span>
                  </span>
                  <span className="shrink-0 tabular-nums">
                    <span className="font-semibold text-gray-900">{formatNum(r.value)}</span>
                    <span className="ml-1.5 text-[10px] text-gray-500">{((r.value / total) * 100).toFixed(1)}%</span>
                  </span>
                </div>
                <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${Math.max((r.value / max) * 100, 2)}%`, backgroundColor: VIEWS_COLOR }}
                  />
                </div>
              </Tag>
            </li>
          );
        })}
      </ul>
      {rows.length > limit && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-2 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
        >
          {expanded ? '−' : `+${rows.length - limit}`}
        </button>
      )}
    </div>
  );
};

/**
 * Swiss canton cartogram — one equal-size tile per canton, shaded by views.
 * Equal tiles keep small, dense cantons (BS, ZG) as readable as large ones.
 */
export const CantonTileMap = ({ rows, selected, onSelect, unitLabel }) => {
  const byCode = useMemo(() => new Map(rows.map((r) => [r.region, r.views])), [rows]);
  const max = rows.reduce((m, r) => (r.region === 'Unknown' ? m : Math.max(m, r.views)), 0);
  return (
    <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(9, minmax(0, 1fr))' }}>
      {Object.entries(CANTONS).map(([code, c]) => {
        const v = byCode.get(code) || 0;
        const isSel = selected === code;
        const dark = max && v / max > 0.45;
        return (
          <button
            key={code}
            type="button"
            onClick={() => onSelect?.(isSel ? null : code)}
            title={`${c.name}: ${formatNum(v)} ${unitLabel}`}
            style={{ gridColumn: c.col + 1, gridRow: c.row + 1, backgroundColor: shade(v, max) }}
            className={`[aspect-ratio:1] rounded-md flex flex-col items-center justify-center text-[10px] font-bold leading-tight transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
              dark ? 'text-white' : 'text-gray-700'
            } ${isSel ? 'ring-2 ring-offset-1 ring-gray-900' : ''}`}
          >
            {code}
            {v > 0 && <span className="font-medium opacity-90 tabular-nums">{v >= 1000 ? `${Math.round(v / 100) / 10}k` : v}</span>}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Weekday × hour heatmap (Zurich time). `data` = [{ day: 1..7, hour, views }]
 * with day 1 = Monday.
 */
export const WeekHourHeatmap = ({ data, dayLabels, unitLabel }) => {
  const grid = useMemo(() => {
    const g = Array.from({ length: 7 }, () => Array(24).fill(0));
    for (const c of data || []) if (c.day >= 1 && c.day <= 7) g[c.day - 1][c.hour] = c.views;
    return g;
  }, [data]);
  const max = grid.flat().reduce((m, v) => Math.max(m, v), 0);
  return (
    <div className="overflow-x-auto">
      <div className="min-w-[520px]">
        {grid.map((row, d) => (
          <div key={d} className="flex items-center gap-[2px] mb-[2px]">
            <span className="w-8 shrink-0 text-[10px] text-gray-500">{dayLabels[d]}</span>
            {row.map((v, h) => (
              <div
                key={h}
                title={`${dayLabels[d]} ${String(h).padStart(2, '0')}:00 — ${formatNum(v)} ${unitLabel}`}
                className="flex-1 h-5 rounded-[3px]"
                style={{ backgroundColor: shade(v, max) }}
              />
            ))}
          </div>
        ))}
        <div className="flex gap-[2px] pl-8">
          {Array.from({ length: 24 }, (_, h) => (
            <span key={h} className="flex-1 text-center text-[9px] text-gray-400">
              {h % 3 === 0 ? String(h).padStart(2, '0') : ''}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
